<template>
    <div v-if="loading" class="text-center py-10">
        <vx-icon-loading class="w-9 h-9 animate-loading-spin"></vx-icon-loading>
    </div>
    <div v-else>
        <div v-if="list.length >= 1" class="text-slate-500 font-extrabold mb-1">
            When {{ props.action === 'add' ? 'adding' : 'removing' }}
            {{ props.resources.length > 1 ? 'these resources' : 'this resource' }}:
        </div>
        <div class="flex flex-col gap-4">
            <flow-resource
                v-for="resource in props.resources"
                :key="'recource.' + resource.type + '.' + resource.id"
                :resource="resource"
                simple
            ></flow-resource>
        </div>
        <template v-if="list.length >= 1">
            <div class="mt-8 text-slate-500 font-extrabold mb-1">
                These related resources will also be {{ props.action === 'add' ? 'added' : 'removed' }}:
            </div>
            <div class="flex flex-col gap-4">
                <flow-resource
                    v-for="dependency in list"
                    :key="'dependency.' + dependency.type + '.' + dependency.id"
                    :resource="dependency"
                    simple
                ></flow-resource>
            </div>
        </template>
    </div>
</template>

<script setup>
import { VxIconLoading } from '@voxie/frontend-components';
import { onMounted, ref } from 'vue';
import { useToasted } from '~/composables/useToasted';
import { automationRulesClient, campaignsClient, segmentsClient } from '~/services';
import FlowResource from './FlowResource.vue';
import { useFlow } from './useFlow';
import { HTTP_INTERNAL_SERVER_ERROR } from '~/support/HttpStatuses';

const teamId = Spark.state.currentTeam.id;

const toasted = useToasted();

const props = defineProps({
    action: {
        type: String,
        default: 'add',
    },
    resources: {
        type: Array,
        required: true,
    },
});

const flow = useFlow();
const loading = ref(false);
const list = ref([]);
const truncatedResource = ref();

const fetchDependencies = async () => {
    loading.value = true;

    try {
        for (const resource of props.resources || []) {
            let client;
            if (resource.type === 'automation') {
                client = automationRulesClient.dependencies(teamId, resource.id);
            } else if (resource.type === 'campaign') {
                client = campaignsClient.dependencies(teamId, resource.id);
            } else if (resource.type === 'segment') {
                client = segmentsClient.dependencies({ teamId }, resource.id);
            }

            const resourceDepsResponse = (await client)?.data;

            // If truncated and adding do not perform any other actions.
            if (props.action === 'add' && resourceDepsResponse?.truncated) {
                const resourceResponse = await flow.fetchResource(resource.type, resource.id);

                truncatedResource.value = {
                    type: resource.type,
                    id: resource.id,
                    name: resourceResponse.name,
                };

                return;
            }

            let resourceDeps = (resourceDepsResponse?.data || []).filter((dependency) => {
                return (
                    !props.resources.some((r) => r.id === dependency.id && r.type === dependency.type) &&
                    !list.value.some((r) => r.id === dependency.id && r.type === dependency.type)
                );
            });

            // Filter deps already in flow on add action only.
            if (props.action === 'add') {
                resourceDeps = resourceDeps.filter(
                    (dependency) =>
                        !flow.sections.value.some((section) =>
                            section.resources.some((res) => res.id === dependency.id && res.type === dependency.type)
                        )
                );
            }

            list.value = list.value.concat(resourceDeps);
        }

        await flow.fetchResources(list.value);
    } catch (e) {
        console.error(e);
        if (!e?.response?.status || e.response.status >= HTTP_INTERNAL_SERVER_ERROR) {
            toasted.global.platform_error();
            return;
        }

        toasted.error(e.response?.data?.message);
    } finally {
        loading.value = false;
    }
};

onMounted(() => {
    fetchDependencies();
});

defineExpose({
    loading: loading,
    list: list,
    truncatedResource: truncatedResource,
});
</script>
